/**
 * Lock Manager
 * Manages PList locks during simulation
 * Since the system has no automatic lock timeout, we must handle cleanup ourselves
 */

import { getSharedRateLimiter, SimulatorRateLimiter } from './RateLimiter';
import { AuthService } from '../../../../../services/authService';
import { LockInfo } from '../types';

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedPost = async (url: string, body?: object): Promise<Response> => {
  return AuthService.authenticatedPost(url, body);
};

export class LockManager {
  private locks: Map<number, LockInfo> = new Map();
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;
  private readonly rateLimiter: SimulatorRateLimiter;
  private readonly companyNo: string;

  constructor(companyNo: string) {
    this.companyNo = companyNo;
    this.rateLimiter = getSharedRateLimiter();
  }

  /**
   * Start periodic lock cleanup
   */
  start(cleanupIntervalMs: number = 5 * 60 * 1000): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    this.cleanupInterval = setInterval(
      () => this.cleanupStaleLocks(),
      cleanupIntervalMs
    );

    console.log(`[LockManager] Started with cleanup interval of ${cleanupIntervalMs}ms`);
  }

  /**
   * Stop the lock manager
   */
  stop(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    console.log('[LockManager] Stopped');
  }

  /**
   * Acquire a lock on a PList
   */
  async acquireLock(pListId: number, operatorId: string): Promise<boolean> {
    try {
      await this.rateLimiter.throttle();
      const response = await authenticatedPost(
        `${API_BASE_URL}/picking/lists/${pListId}/lock`,
        { operatorId }
      );

      if (!response.ok) {
        console.error(`[LockManager] Failed to acquire lock on PList ${pListId}: ${response.statusText}`);
        return false;
      }

      this.locks.set(pListId, {
        pListId,
        operatorId,
        lockedAt: new Date()
      });

      console.log(`[LockManager] Acquired lock on PList ${pListId} for operator ${operatorId}`);
      return true;
    } catch (error) {
      console.error(`[LockManager] Failed to acquire lock on PList ${pListId}:`, error);
      return false;
    }
  }

  /**
   * Release a lock on a PList
   */
  async releaseLock(pListId: number, operatorId: string): Promise<boolean> {
    try {
      await this.rateLimiter.throttle();
      const response = await authenticatedPost(
        `${API_BASE_URL}/picking/lists/${pListId}/unlock`,
        { operatorId }
      );

      if (!response.ok) {
        console.error(`[LockManager] Failed to release lock on PList ${pListId}: ${response.statusText}`);
        return false;
      }

      this.locks.delete(pListId);
      console.log(`[LockManager] Released lock on PList ${pListId}`);
      return true;
    } catch (error) {
      console.error(`[LockManager] Failed to release lock on PList ${pListId}:`, error);
      return false;
    }
  }

  /**
   * Release all locks held by the simulator
   */
  async releaseAllLocks(): Promise<void> {
    console.log(`[LockManager] Releasing ${this.locks.size} locks...`);

    const lockEntries = Array.from(this.locks.entries());

    for (const [pListId, lock] of lockEntries) {
      try {
        await this.releaseLock(pListId, lock.operatorId);
      } catch (error) {
        console.error(`[LockManager] Failed to release lock on PList ${pListId}:`, error);
      }
    }

    this.locks.clear();
    console.log('[LockManager] All locks released');
  }

  /**
   * Clean up stale locks (locks older than maxAgeMinutes)
   */
  async cleanupStaleLocks(maxAgeMinutes: number = 30): Promise<void> {
    const now = new Date();
    const staleLocks: LockInfo[] = [];

    const lockEntries = Array.from(this.locks.entries());
    for (const [pListId, lock] of lockEntries) {
      const ageMinutes = (now.getTime() - lock.lockedAt.getTime()) / 60000;
      if (ageMinutes > maxAgeMinutes) {
        staleLocks.push(lock);
      }
    }

    if (staleLocks.length > 0) {
      console.warn(`[LockManager] Found ${staleLocks.length} stale lock(s), releasing...`);

      for (const lock of staleLocks) {
        console.warn(`[LockManager] Releasing stale lock on PList ${lock.pListId} (age: ${((now.getTime() - lock.lockedAt.getTime()) / 60000).toFixed(1)} min)`);
        await this.releaseLock(lock.pListId, lock.operatorId);
      }
    }
  }

  /**
   * Check if a PList is locked by the simulator
   */
  isLockedBySimulator(pListId: number): boolean {
    return this.locks.has(pListId);
  }

  /**
   * Get all current locks
   */
  getLocks(): LockInfo[] {
    return Array.from(this.locks.values());
  }

  /**
   * Get the number of active locks
   */
  getLockCount(): number {
    return this.locks.size;
  }

  /**
   * Get lock info for a specific PList
   */
  getLockInfo(pListId: number): LockInfo | undefined {
    return this.locks.get(pListId);
  }
}

// Singleton instance
let sharedLockManager: LockManager | null = null;

export const getSharedLockManager = (companyNo: string): LockManager => {
  if (!sharedLockManager || sharedLockManager['companyNo'] !== companyNo) {
    sharedLockManager = new LockManager(companyNo);
  }
  return sharedLockManager;
};

export const resetSharedLockManager = async (): Promise<void> => {
  if (sharedLockManager) {
    sharedLockManager.stop();
    await sharedLockManager.releaseAllLocks();
    sharedLockManager = null;
  }
};
